import { ArrowUpRight, Check, X, PackageCheck, ClipboardList, Tag, ListChecks, Users } from "lucide-react";
import { HeroFade, Reveal } from "../components/Motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { STRIPE_EU_PACK_URL, EUR_LEX_GSPR_URL, EMAIL } from "../constants";

const PayButton = ({ testid, className = "" }) => (
  <a
    href={STRIPE_EU_PACK_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md ${className}`}
  >
    Pay $399 — One-Time Fee
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const FOR_LIST = [
  "US or UK brands selling physical consumer goods to the EU",
  "Selling on Amazon.de / .fr / .it or your own EU checkout",
  "Home, kitchen, baby, toys, sports, tools, beauty, pet",
];

const NOT_FOR_LIST = [
  "Not food",
  "Not software",
  "Not a company already established in the EU that already has an RP",
];

const INCLUDED = [
  {
    num: "01",
    icon: PackageCheck,
    title: "Scope map of your SKUs",
    body: "For the SKUs you send: in GPSR or not, and what Amazon is asking for.",
  },
  {
    num: "02",
    icon: ClipboardList,
    title: "Amazon product-safety field checklist",
    body: "PS01–PS06 style fields, in plain English.",
  },
  {
    num: "03",
    icon: Tag,
    title: "Label layout",
    body: "Where the RP name and EU address must appear. You print it.",
  },
  {
    num: "04",
    icon: ListChecks,
    title: "Product-risk questionnaire template",
    body: "A simple checklist — not a legal opinion, not a lab test.",
  },
  {
    num: "05",
    icon: Users,
    title: "RP matchmaking session",
    body: "A 45-minute English session to pick an existing EU-established RP provider and complete that provider's signup pack. The RP contract and yearly fee (~€150–€500, confirm with them) are paid to the RP, not to us. We never act as the RP.",
  },
];

const NOT_INCLUDED = [
  "We do not become your EU Responsible Person (a US LLC cannot).",
  "We do not test products, issue CE, write a lawyer letter, or talk to Amazon Seller Support for you.",
  "We do not file taxes, EPR, or packaging as your accountant.",
  "We do not guarantee Amazon reinstates any ASIN.",
];

const TIMELINE = [
  {
    day: "Day 0",
    title: "Pay and intake",
    body: "Pay $399 at the Stripe link. Then a short intake form: SKU list, marketplace, current label photo, and whether Amazon already suppressed you.",
  },
  {
    day: "Day 1–2",
    title: "Scope map + field checklist",
    body: "You receive the SKU scope map and the Amazon product-safety field checklist.",
  },
  {
    day: "Day 3–5",
    title: "Label, questionnaire, RP shortlist",
    body: "Label layout, the risk questionnaire, and a shortlist of 2–3 EU-established RP providers.",
  },
  {
    day: "Day 5–7",
    title: "Session + handoff",
    body: "The 45-minute English session and the final handoff. RP fees are extra and paid to the RP.",
  },
];

const FAQ = [
  {
    q: "Can you be our Responsible Person?",
    a: "No. We never act as the EU Responsible Person — a US LLC cannot.",
  },
  {
    q: "Is this legal advice?",
    a: "No. The governing text is Regulation (EU) 2023/988. A lawyer or your Responsible Person applies the rules to your facts.",
  },
  {
    q: "We already have an EU company — do we need this?",
    a: "You may not need this pack, and we will say so. If the scope check shows you are clearly out of scope, unused work is refunded.",
  },
  {
    q: "Amazon already blocked us. What then?",
    a: "Same pack. We still cannot file the Amazon appeal for you.",
  },
];

export default function EuSellThrough() {
  return (
    <main data-testid="eu-sell-through-page" className="pb-28">
      <section className="relative overflow-hidden px-6 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl pb-20">
          <HeroFade delay={0.1}>
            <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
              EY TECH LLC — EU Sell-Through Pack
            </p>
          </HeroFade>
          <h1 data-testid="eu-pack-heading" className="max-w-4xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
            <HeroFade delay={0.2}>
              Your Amazon EU listing needs an EU Responsible Person.
            </HeroFade>
            <HeroFade delay={0.35}>
              <em className="text-blue-600">We package the file.</em>
            </HeroFade>
          </h1>
          <HeroFade delay={0.5}>
            <p data-testid="eu-pack-sub" className="mt-8 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg">
              GPSR has applied since 13 Dec 2024. If you are a US/UK brand placing
              consumer goods on the EU market, Amazon can suppress the ASIN until an
              EU-established Responsible Person is on the label and in the
              product-safety fields.
            </p>
          </HeroFade>
          <HeroFade delay={0.6} className="mt-8">
            <span
              data-testid="eu-pack-price-chip"
              className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-soft"
            >
              $399 once <span className="text-slate-300">·</span> Stripe <span className="text-slate-300">·</span> remote <span className="text-slate-300">·</span> English <span className="text-slate-300">·</span> 5–7 days
            </span>
          </HeroFade>
          <HeroFade delay={0.7} className="mt-8">
            <PayButton testid="pay-stripe-button" />
            <p className="mt-5 max-w-xl text-xs leading-relaxed text-slate-500">
              We are not an EU Responsible Person, not a lawyer, not a notified body,
              not Amazon.
            </p>
            <p data-testid="eu-pack-hero-disclaimer" className="mt-2 text-xs leading-relaxed text-slate-500">
              EY TECH LLC is not affiliated with Ernst &amp; Young (EY).
            </p>
          </HeroFade>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <h2 data-testid="who-its-for-heading" className="mb-12 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            Who it's for.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl bg-white p-8 shadow-soft md:p-10">
              <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                Built for
              </p>
              <ul className="space-y-4">
                {FOR_LIST.map((item, i) => (
                  <li key={item} data-testid={`for-item-${i}`} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600 md:text-base">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl bg-slate-50 p-8 md:p-10">
              <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-slate-500">
                Not for
              </p>
              <ul className="space-y-4">
                {NOT_FOR_LIST.map((item, i) => (
                  <li key={item} data-testid={`not-for-item-${i}`} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600 md:text-base">
                    <X className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
            The Pack
          </p>
          <h2 data-testid="included-heading" className="mb-16 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            What $399 includes.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((item, i) => (
            <Reveal key={item.num} delay={i * 0.08} className={i === 4 ? "md:col-span-2 lg:col-span-1" : ""}>
              <article
                data-testid={`included-item-${i}`}
                className="group flex h-full flex-col gap-6 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift md:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-4xl font-medium italic text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                    {item.num}
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 transition-colors duration-300 group-hover:bg-blue-100">
                    <item.icon className="h-5 w-5 text-blue-600" />
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">{item.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
          <div>
            <Reveal>
              <h2 data-testid="not-included-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-400 md:text-5xl">
                What it is <em>not.</em>
              </h2>
            </Reveal>
            <ul className="flex flex-col gap-4">
              {NOT_INCLUDED.map((item, i) => (
                <Reveal key={item} delay={i * 0.08}>
                  <li
                    data-testid={`not-included-item-${i}`}
                    className="flex items-start gap-4 rounded-2xl bg-slate-50 px-6 py-5 transition-colors duration-300 hover:bg-slate-100"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-soft">
                      <X className="h-4 w-4 text-slate-400" />
                    </span>
                    <span className="text-sm leading-relaxed text-slate-600 md:text-base">
                      {item}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <Reveal>
              <h2 data-testid="how-it-works-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-900 md:text-5xl">
                How it <em className="text-blue-600">works.</em>
              </h2>
            </Reveal>
            <div className="flex flex-col gap-6">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.day} delay={i * 0.08}>
                  <article data-testid={`timeline-${i}`} className="flex gap-6 rounded-2xl bg-white p-6 shadow-soft md:p-8">
                    <span className="w-20 shrink-0 font-serif text-lg font-medium italic text-blue-600">
                      {t.day}
                    </span>
                    <div>
                      <h3 className="font-serif text-xl font-medium tracking-tight text-slate-900">
                        {t.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.body}</p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <h2 data-testid="faq-heading" className="mb-12 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            Straight answers.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="rounded-2xl bg-white px-8 shadow-soft" data-testid="faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border-slate-200">
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-left font-serif text-lg font-medium tracking-tight text-slate-900 hover:text-blue-600 hover:no-underline md:text-xl"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm leading-relaxed text-slate-600 md:text-base">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <Reveal>
          <div className="grid grid-cols-1 items-center gap-10 rounded-2xl bg-slate-900 p-10 text-white shadow-lift md:grid-cols-2 md:p-14">
            <div>
              <h2 data-testid="final-cta-heading" className="font-serif text-3xl font-medium leading-[1.1] tracking-tight md:text-5xl">
                Start this <em className="text-blue-400">week.</em>
              </h2>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-300 md:text-base">
                One payment, one domain of work, five to seven days. Card via Stripe —
                no other payment methods, and we never see your card details.
              </p>
            </div>
            <div className="flex flex-col items-start gap-6 md:items-end">
              <span className="font-serif text-5xl font-medium tracking-tight md:text-6xl">$399</span>
              <PayButton testid="final-pay-button" className="bg-white !text-slate-900 hover:bg-blue-50" />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="eu-pack-company-block" className="mt-10 flex flex-col gap-3 rounded-2xl bg-white p-8 text-sm text-slate-600 shadow-soft md:p-10">
            <p>
              <span className="font-semibold text-slate-900">EY TECH LLC</span>, United States ·{" "}
              <a href={`mailto:${EMAIL}`} data-testid="eu-pack-email-link" className="text-blue-600 hover:underline">
                {EMAIL}
              </a>
            </p>
            <p>EY TECH LLC is not affiliated with Ernst &amp; Young (EY). This site is not legal advice.</p>
            <p>
              Governing text:{" "}
              <a
                href={EUR_LEX_GSPR_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="eu-pack-regulation-link"
                className="text-blue-600 hover:underline"
              >
                Regulation (EU) 2023/988 (EUR-Lex)
              </a>
            </p>
          </div>
        </Reveal>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/70 bg-cream/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4 md:px-10">
          <div className="hidden items-baseline gap-3 md:flex">
            <span className="font-serif text-2xl font-medium text-slate-900">$399</span>
            <span className="text-sm text-slate-500">
              EU Sell-Through Pack · 5–7 days
            </span>
          </div>
          <a
            href={STRIPE_EU_PACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-pay-button"
            className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md md:w-auto"
          >
            Pay $399 — One-Time Fee
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </main>
  );
}
